import React, { useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, Download } from "lucide-react";
import Swal from "sweetalert2";
import Card from "../components/Card";
import BlueButton from "../components/BlueButton";
import TableSkeleton from "../components/TableSkeleton";
import SimpleTable from "../components/SimpleTable";
import Badge from "../components/Badge";

export default function ReceivedKeysTable({ brand, loginUserID, CompanyID }) {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const rowsPerPage = 10;

  // ✅ Fetch Paginated Data for Table
  const fetchReceiveReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `https://goelectronix.in/api/v1/keys/report`,
        {
          params: {
            receiver_id: loginUserID,
            mode: 1,
            company_id: CompanyID,
            page,
            per_page: rowsPerPage,
          },
        }
      );

      setData(res.data.data || []);
      setTotalPages(res.data.last_page || 1);
    } catch (err) {
      console.error("Failed to fetch receive reports:", err);
      setError("Failed to load receive report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loginUserID || !CompanyID) return;
    fetchReceiveReports();
  }, [loginUserID, CompanyID, page]);

  // ✅ CSV Download Handler (Full Data)
  const handleDownload = async () => {
    try {
      setDownloading(true);

      // fetch all data with large per_page
      const res = await axios.get(`https://goelectronix.in/api/v1/keys/report`, {
        params: {
          receiver_id: loginUserID,
          mode: 1,
          company_id: CompanyID,
          page: 1,
          per_page: 10000, // ✅ get all
        },
      });

      const rows = res.data?.data || [];
      if (!rows.length) {
        Swal.fire("No data to download", "", "info");
        return;
      }

      // ✅ Define CSV Fields
      const selectedFields = [
        "created_at",
        "sender_name",
        "sender_code",
        "key_type",
        "package_name",
        "qty",
      ];

      const headers = [
        "Date",
        "Sender Name",
        "Sender Code",
        "Type",
        "Package",
        "Count",
      ];

      // ✅ Create CSV Data
      const csvRows = [
        headers.join(","), // Header row
        ...rows.map((r) =>
          [
            new Date(r.created_at).toLocaleDateString(),
            r.sender_name || "",
            r.sender_code || "",
            r.key_type === 1 ? "Regular" : "Smart",
            r.package_name || "",
            r.qty || 0,
          ]
            .map((v) => `"${v}"`)
            .join(",")
        ),
      ];

      // ✅ Create downloadable CSV file
      const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `received_keys_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Download failed:", err);
      Swal.fire("Failed to download data", "", "error");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card  title="Received Keys">
      {/* 🔹 Header Buttons */}
      <div className="flex justify-end gap-2 flex-wrap mb-3">
        {/* ✅ Download CSV */}
        <BlueButton
          
          onClick={handleDownload}
          disabled={downloading}
          className={`inline-flex items-center gap-1 ${
            downloading ? "opacity-60 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
          }`}
        >
          {downloading ? (
            <>
              <Download size={14} className="animate-bounce" /> Downloading...
            </>
          ) : (
            <>
              <Download size={14} /> Download
            </>
          )}
        </BlueButton>

        {/* Refresh */}
        <BlueButton
          
          onClick={fetchReceiveReports}
          className="inline-flex items-center gap-1"
        >
          <RefreshCw size={14} /> Refresh
        </BlueButton>
      </div>

      {/* 🔹 Table */}
      {loading ? (
        <TableSkeleton
          rows={data?.length || rowsPerPage}
          headers={[
            "Sr No.",
            "Date",
            "Sender (Code)",
            "Type",
            "Package",
            "Count",
            "Key rate", 
            "Total Amount",
            "Status",
          ]}
        />
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : data.length === 0 ? (
        <p>No data found</p>
      ) : (
        <>
          <SimpleTable
            headers={[
              "Sr No.",
              "Date",
              "Sender (Code)",
              "Type",
              "Package",
              "Count",
              "Key rate", 
              "Total Amount",
              "Status",
            ]}
            rows={data.map((rep, i) => [
              (page - 1) * rowsPerPage + i + 1,
              new Date(rep.created_at).toLocaleDateString(),
              `${rep.sender_name} (${rep.sender_code || "N/A"})`,
              rep.key_type === 1 ? "Regular" : "Smart",
              rep.package_name,
              rep.qty,
              `₹ ${rep.key_rate}`,
              `₹ ${rep.amount}`,
              <Badge label="Received" color="blue" />,
            ])}
          />

          {/* 🔹 Pagination */}
          <div className="flex gap-2 justify-end mt-3 items-center flex-wrap">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="px-2 py-1 border rounded disabled:opacity-50 hover:bg-gray-100"
            >
              Prev
            </button>

            {(() => {
              const totalToShow = 5;
              let start = Math.max(1, page - Math.floor(totalToShow / 2));
              let end = Math.min(totalPages, start + totalToShow - 1);
              if (end - start < totalToShow - 1) {
                start = Math.max(1, end - totalToShow + 1);
              }

              return (
                <>
                  {start > 1 && (
                    <>
                      <button
                        className="px-2 py-1 border rounded hover:bg-gray-100"
                        onClick={() => setPage(1)}
                      >
                        1
                      </button>
                      {start > 2 && <span className="px-2">...</span>}
                    </>
                  )}

                  {Array.from({ length: end - start + 1 }, (_, i) => start + i).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`px-2 py-1 border rounded transition-colors ${
                        page === p
                          ? "bg-blue-500 text-white border-blue-500"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      {p}
                    </button>
                  ))}

                  {end < totalPages && (
                    <>
                      {end < totalPages - 1 && <span className="px-2">...</span>}
                      <button
                        className="px-2 py-1 border rounded hover:bg-gray-100"
                        onClick={() => setPage(totalPages)}
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </>
              );
            })()}

            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="px-2 py-1 border rounded disabled:opacity-50 hover:bg-gray-100"
            >
              Next
            </button>
          </div>
        </>
      )}
    </Card>
  );
}